/** @jsx jsx */
import { css, jsx } from '@emotion/react';
// eslint-disable-next-line no-unused-vars
import React, { useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { hierarchy, pack } from 'd3';
import { AnimatePresence, motion } from 'framer-motion';
import { useTooltip } from 'components/chartTooltip/useTooltip';
import ChartTooltip from 'components/chartTooltip';
import debounce from 'lodash-es/debounce';
import { primaryColor } from 'styles/colors';
import { rgba } from 'polished';

export const CirclePack = ({ getColorScale, dimensions, data, showTopLabelsOnly = true }) => {
	const { height, width } = dimensions;

	const [toolTip, setupTrigger] = useTooltip();

	const [visibleTextNodes, setVisibleTextNodes] = useState(new Set());

	const textRefs = useRef({});

	function getChildren(d) {
		return d.children;
	}

	function getValue(d) {
		if (!d.hasOwnProperty("value")) {
			//calculate it and set it once. Since this function gets called a lot do some form of caching.
			if (d.hasOwnProperty("children")) {
				d.value = d.children?.reduce((sum, { value }) => sum + value, 0);
			}
			else d.value = 0;
		}
		return d.value;
	}

	function getName(d) {
		return d.key;
	}

	const { nodes, root } = useMemo(() => {
		const root = hierarchy(data, getChildren)
			.sum(getValue)
			.sort((a, b) => getValue(b) - getValue(a));

		// Create packer & pack circles. Ensure dimensions are not 0
		const circlePacker = pack().size([width||1, height||1]).padding(10);
		circlePacker(root);

		return { nodes: root.descendants(), root };
	}, [data, width, height]);

	function getKeyFromNode(node) {
		const nodePath = node.path(root);
		if (nodePath.length === 0) {
			return 'root';
		} else {
			return nodePath.reduce((accumulator, node) => {
				accumulator += '/' + getName(node.data);
				return accumulator;
			}, '');
		}
	}

	const topLevelKeys = root.children?.reduce(
		(keys, child) => ({
			...keys,
			[getKeyFromNode(child)]: true,
		}),
		{}
	);

	const checkIntersections = debounce(function () {
		function doesIntersect(n1, n2) {
			const aLeftOfB = n1.right < n2.left;
			const aRightOfB = n1.left > n2.right;
			const aAboveB = n1.top > n2.bottom;
			const aBelowB = n1.bottom < n2.top;

			return !(aLeftOfB || aRightOfB || aAboveB || aBelowB);
		}

		const _visibleNodes = new Set(
			Object.entries(textRefs.current)
				.map(([key, el]) => {
					const { top, right, bottom, left } = el.getBoundingClientRect();
					//getBoundingClientRect doesn't return a true Obj, so create a true object and include key
					return { key, top, right, bottom, left };
				})
				.filter((n, i, arr) => {
					if (n.hidden) return false; //if hidden, return false so its not visible

					arr.slice(i + 1).forEach((_n) => {
						if (doesIntersect(n, _n)) {
							_n.hidden = true;
						}
					});
					return true; //not hidden, so return true to include in visible nodes
				})
				.map(({ key }) => key)
		);

		//Set state so future rerenders correctly set visibility.
		setVisibleTextNodes(_visibleNodes);
	}, 100);

	const _setTextRef = (key) => (r) => {
		if (r) textRefs.current[key] = r;
		else delete textRefs.current[key];
	};

	return (
		<React.Fragment>
			<div>
				<svg cy-test="circle-pack-data" width={width} height={height}>
					{/* Circles */}
					<AnimatePresence>
						{nodes.map((node) => {
							const startEndState = {
								cx: width / 2,
								cy: height / 2,
								r: 0,
							};
							return (
								<motion.circle
									key={getKeyFromNode(node)}
									initial={startEndState}
									animate={{
										cx: node.x,
										cy: node.y,
										r: makeSafeForSVG(node.r),
									}}
									exit={startEndState}
									css={css`
										fill: ${rgba(primaryColor, 0.5)};
										stroke: rgba(255, 255, 255, 0.26);
										opacity: 0.5;
									`}
								/>
							);
						})}
					</AnimatePresence>
					{/* Labels */}
					<AnimatePresence>
						{nodes.map((node) => {
							const nodeIsParent = !!node.children;
							// Make the label appear above nodes with children
							const yOffset = 10;
							const y = nodeIsParent ? node.y - makeSafeForSVG(node.r) + yOffset : node.y;
							const x = node.x;
							const startEndState = {
								x,
								y,
								opacity: 0,
							};
							const key = getKeyFromNode(node);
							if (typeof topLevelKeys === 'undefined' || (!topLevelKeys[key] && showTopLabelsOnly)) {
								return null;
							}
							return (
								<motion.text
									onAnimationComplete={checkIntersections}
									ref={_setTextRef(key)}
									key={key}
									initial={startEndState}
									animate={{
										x,
										y,
										opacity: 1,
									}}
									exit={startEndState}
									css={css`
										fill: black;
										dominant-baseline: middle;
										text-anchor: middle;
										font-weight: bold;
										visibility: ${visibleTextNodes.has(key) ? 'visible' : 'hidden'};
									`}
								>
									{node.data.key}
								</motion.text>
							);
						})}
					</AnimatePresence>
					{/*Tooltips*/}
					{nodes.map((node) => {
						if(!node.data.key) return null; 
						return (
							<motion.circle
								key={getKeyFromNode(node)}
								cx={node.x}
								cy={node.y}
								r={makeSafeForSVG(node.r)}
								css={css`
									fill: transparent;
									stroke: transparent;
								`}
								{...setupTrigger(<ChartTooltip title={getName(node.data)} content={{ Size: getValue(node.data) }} />)}
							/>
						);
					})}
				</svg>
				{toolTip}
			</div>
		</React.Fragment>
	);
};

function makeSafeForSVG(anyTypeOfNumber) {
	return anyTypeOfNumber || 0;
}

CirclePack.propTypes = {
	data: PropTypes.any,
	dimensions: PropTypes.object,
	showTopLabelsOnly: PropTypes.bool,
};
